import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../i18n/LanguageContext';
import { getConfig } from '../utils/config';
import { withTxToast } from '../utils/txToast';
import {
  Image as ImageIcon,
  Loader,
  CheckCircle,
  AlertCircle,
  Upload,
  X,
  FileText,
  ExternalLink,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Constantes de validación
// ---------------------------------------------------------------------------
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_TYPES = [...ALLOWED_IMAGE_TYPES, 'application/pdf'];
const ALLOWED_EXTENSIONS_LABEL = 'JPG, PNG, GIF, WEBP, PDF';

/** Las imágenes se comprimen en servidor; aceptamos hasta 20 MB en crudo. */
const MAX_IMAGE_RAW_BYTES = 20 * 1024 * 1024;
/** Los PDFs no se pueden comprimir: límite estricto de 5 MB. */
const MAX_PDF_BYTES = 5 * 1024 * 1024;

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
const MintNFT = ({ nftContract, nftContractAddress, onMinted }) => {
  const { web3, account, contract } = useWeb3();
  const { t } = useLanguage();
  const config = getConfig();

  const [mintPrice, setMintPrice] = useState('0');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const [nftName, setNftName] = useState('');
  const [nftDescription, setNftDescription] = useState('');

  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [uploadError, setUploadError] = useState('');

  const [loading, setLoading] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [mintError, setMintError] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => {
    loadMintPrice();
  }, [nftContract]);

  const validateNftContract = async () => {
    if (!nftContract || !web3 || !nftContractAddress) return;

    const code = await web3.eth.getCode(nftContractAddress);
    if (!code || code === '0x' || code === '0x0') {
      throw new Error('No hay un contrato desplegado en la direccion NFT configurada para esta red. Revisa red/chainId y direccion.');
    }

    try {
      // If this call fails, the address/ABI/network combination is inconsistent.
      await nftContract.methods.mintPrice().call();
    } catch (_) {
      throw new Error('La direccion NFT configurada no coincide con el ABI esperado. Verifica VITE_NFT_CONTRACT_ADDRESS y la red BFPE en MetaMask.');
    }
  };

  const loadMintPrice = async () => {
    if (!nftContract || !web3) return;
    try {
      await validateNftContract();
      const price = await nftContract.methods.mintPrice().call();
      setMintPrice(web3.utils.fromWei(price, 'ether'));
    } catch (err) {
      console.error('Error loading mint price:', err);
      setMintError(err.message || t('mintNFT.mintError'));
    }
  };

  // -------------------------------------------------------------------------
  // Validación y selección de archivo
  // -------------------------------------------------------------------------
  const validateAndSetFile = useCallback((file) => {
    if (!file) return;
    setUploadError('');
    setUploadedUrl('');
    setPreview(null);
    setTxHash('');

    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError(t('mintNFT.errFormat'));
      return;
    }

    const isPdf = file.type === 'application/pdf';
    if (isPdf && file.size > MAX_PDF_BYTES) {
      setUploadError(t('mintNFT.errPdfSize'));
      return;
    }
    if (!isPdf && file.size > MAX_IMAGE_RAW_BYTES) {
      setUploadError(t('mintNFT.errImgSize'));
      return;
    }

    setSelectedFile(file);

    if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  }, []);

  const handleFileInputChange = (e) => {
    validateAndSetFile(e.target.files[0] || null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    validateAndSetFile(e.dataTransfer.files[0] || null);
  };

  const removeFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setUploadedUrl('');
    setUploadError('');
    setMintError('');
    setTxHash('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // -------------------------------------------------------------------------
  // Subida al servidor
  // -------------------------------------------------------------------------
  const handleUpload = async () => {
    if (!selectedFile) return;
    if (!nftName.trim()) {
      setUploadError(t('mintNFT.errNoName'));
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', nftName.trim());
      formData.append('description', nftDescription.trim());
      const res = await fetch('/api/upload', { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('mintNFT.uploadError'));
      setUploadedUrl(data.metadataUrl);
    } catch (err) {
      setUploadError(err.message || t('mintNFT.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Minteo del NFT
  // -------------------------------------------------------------------------
  const handleMint = async (e) => {
    e.preventDefault();
    if (!nftContract || !account || !web3) {
      setMintError(t('mintNFT.errNoWallet'));
      return;
    }
    if (!uploadedUrl) {
      setMintError(t('mintNFT.errNoFile'));
      return;
    }

    setLoading(true);
    setMintError('');
    setTxHash('');

    try {
      await validateNftContract();

      // Fetch mintPrice directly from contract to avoid stale state
      const priceWei = await nftContract.methods.mintPrice().call();
      const priceEth = web3.utils.fromWei(priceWei, 'ether');

      // Paso 1: aprobar el contrato NFT para gastar los tokens BFPE
      // Siempre aprobamos si el precio > 0, usando el precio exacto del contrato
      if (BigInt(priceWei) > 0n) {
        const currentAllowance = await contract.methods.allowance(account, nftContractAddress).call();
        // Re-aprobar si el allowance actual es menor al precio necesario
        if (BigInt(currentAllowance) < BigInt(priceWei)) {
          await withTxToast(
            () => contract.methods.approve(nftContractAddress, priceWei).send({
              from: account,
              gas: 120000,
              gasPrice: '0',
            }),
            t('mintNFT.approveLoading'),
            t('mintNFT.approveSuccess')
          );
        }
      }

      // Paso 2: minar el NFT
      // 2.1 Preflight para obtener razón de revert más clara antes de enviar tx
      await nftContract.methods.mint(uploadedUrl).call({ from: account });

      // 2.2 Estimar gas dinámicamente y añadir margen de seguridad
      const estimatedGas = await nftContract.methods.mint(uploadedUrl).estimateGas({ from: account });
      const gasWithBuffer = Math.ceil(Number(estimatedGas) * 1.25);

      const tx = await withTxToast(
        () => nftContract.methods.mint(uploadedUrl).send({
          from: account,
          gas: gasWithBuffer,
          gasPrice: '0',
        }),
        t('mintNFT.mintLoading'),
        t('mintNFT.mintSuccess')
      );
      setTxHash(tx.transactionHash);
      onMinted?.();
      setNftName('');
      setNftDescription('');
      setSelectedFile(null);
      setPreview(null);
      setUploadedUrl('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setMintError(err.message || t('mintNFT.mintError'));
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Helpers de UI
  // -------------------------------------------------------------------------
  const isImage = selectedFile && ALLOWED_IMAGE_TYPES.includes(selectedFile.type);
  const willCompress = isImage && selectedFile.size > 5 * 1024 * 1024;

  // -------------------------------------------------------------------------
  // Render — wallet no conectada
  // -------------------------------------------------------------------------
  if (!account) {
    return (
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <ImageIcon className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">{t('mintNFT.title')}</h2>
        </div>
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            {t('mintNFT.noWallet')}
          </p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render — principal
  // -------------------------------------------------------------------------
  return (
    <div className="card">
      <div className="flex items-center space-x-2 mb-6">
        <ImageIcon className="w-6 h-6 text-purple-600" />
        <h2 className="text-2xl font-bold text-gray-800">{t('mintNFT.title')}</h2>
      </div>

      {/* Precio de minado + dirección contrato */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4 space-y-1">
        <p className="text-sm text-blue-800">
          <strong>{t('mintNFT.mintPriceLabel')}</strong> {mintPrice} BFPE
        </p>
        <p className="text-xs text-blue-700 break-all">
          <strong>{t('mintNFT.contractLabel')}</strong>{' '}
          <a
            href={`${config.EXPLORER_URL}/address/${nftContractAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline font-mono"
          >
            {nftContractAddress}
          </a>
        </p>
      </div>

      {/* Nombre y descripción del NFT */}
      <div className="space-y-3 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('mintNFT.nameLabel')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={nftName}
            onChange={(e) => setNftName(e.target.value)}
            placeholder={t('mintNFT.namePlaceholder')}
            maxLength={100}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('mintNFT.descLabel')}
          </label>
          <textarea
            value={nftDescription}
            onChange={(e) => setNftDescription(e.target.value)}
            placeholder={t('mintNFT.descPlaceholder')}
            maxLength={500}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
          />
        </div>
      </div>

      {/* Zona de selección de archivo */}
      {!selectedFile ? (
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-purple-500 bg-purple-50'
              : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
          }`}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
          aria-label={t('mintNFT.fileAriaLabel')}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600 mb-1">
            {t('mintNFT.dragDrop')}
          </p>
          <p className="text-xs text-gray-400">
            {t('mintNFT.formats')}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
            className="hidden"
            onChange={handleFileInputChange}
          />
        </div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          {/* Previsualización de imagen */}
          {preview ? (
            <div className="relative bg-gray-100">
              <img
                src={preview}
                alt={t('mintNFT.previewAlt')}
                className="w-full max-h-48 object-contain"
              />
              <button
                onClick={removeFile}
                className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-red-50"
              aria-label={t('mintNFT.removeAriaLabel')}
              >
                <X className="w-4 h-4 text-red-500" />
              </button>
            </div>
          ) : (
            /* Icono PDF */
            <div className="flex items-center justify-between p-4 bg-red-50">
              <div className="flex items-center space-x-3">
                <FileText className="w-8 h-8 text-red-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">{formatBytes(selectedFile.size)}</p>
                </div>
              </div>
              <button
                onClick={removeFile}
                className="ml-3 p-1 hover:bg-red-100 rounded flex-shrink-0"
                aria-label={t('mintNFT.removeAriaLabel')}
              >
                <X className="w-4 h-4 text-red-500" />
              </button>
            </div>
          )}

          {/* Barra de información del archivo (bajo la imagen) */}
          {preview && (
            <div className="px-4 py-2 bg-gray-50 flex items-center justify-between text-xs text-gray-500 border-t">
              <span className="truncate max-w-[65%]">{selectedFile.name}</span>
              <span>{formatBytes(selectedFile.size)}</span>
            </div>
          )}

          {/* Aviso de compresión */}
          {willCompress && (
            <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                {t('mintNFT.compressionWarning')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Error de subida */}
      {uploadError && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{uploadError}</p>
        </div>
      )}

      {/* Archivo subido correctamente */}
      {uploadedUrl && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-green-800">{t('mintNFT.uploadSuccess')}</p>
            <a
              href={uploadedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-green-700 break-all hover:underline flex items-center gap-1 mt-1"
            >
              {uploadedUrl}
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
            </a>
          </div>
        </div>
      )}

      {/* Botones de acción */}
      <div className="mt-4 space-y-3">
        {/* Botón de subida — aparece cuando hay archivo pero aún no se ha subido */}
        {selectedFile && !uploadedUrl && (
          <button
            onClick={handleUpload}
            disabled={uploading || !!uploadError}
            className="btn-primary w-full flex items-center justify-center space-x-2"
          >
            {uploading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>{t('mintNFT.uploadingBtn')}</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span>{t('mintNFT.uploadBtn')}</span>
              </>
            )}
          </button>
        )}

        {/* Botón de minteo — aparece tras subida exitosa */}
        {uploadedUrl && (
          <form onSubmit={handleMint}>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>{t('mintNFT.mintingBtn')}</span>
                </>
              ) : (
                <>
                  <ImageIcon className="w-5 h-5" />
                  <span>{t('mintNFT.mintBtn', { price: mintPrice })}</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>

      {/* Error de minteo */}
      {mintError && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{mintError}</p>
        </div>
      )}

      {/* Transacción exitosa */}
      {txHash && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
          <p className="text-sm text-green-800 font-medium">{t('mintNFT.mintSuccessTitle')}</p>
            {config.EXPLORER_URL ? (
              <a
                href={`${config.EXPLORER_URL}/tx/${txHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-green-700 hover:underline flex items-center gap-1 mt-1"
              >
                {t('mintNFT.viewExplorer')} <ExternalLink className="w-3 h-3" />
              </a>
            ) : (
              <p className="text-xs text-green-700 mt-1 break-all">TX: {txHash}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MintNFT;
